/**
 * 생성기 한 번 실행 — 세션 확보 → (자료 탭) 자료 등록 → direct_generate 턴 → 문항 적용 → 결과 판정.
 * 자료 등록은 같은 세션에 이미 등록한 자료(다시 시도) 또는 첨부할 때 시작한 사전 등록이 있으면
 * 그것을 쓰고, 없거나 실패했으면 원본으로 등록한다.
 * React 를 모른다. 화면 상태(대기 카드·작업 표시)는 호출하는 훅이 소유한다.
 */
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { createAiBuilderClientRequestId } from '../stores/aiBuilderTurnRequests';
import { type AiGenerateDockRunPhase } from '../types/aiGenerateDock.types';

import {
  appendQuestionSnapshotChanges,
  applyQuestionSnapshotReplace,
  questionListRevisionOf,
  type QuestionSnapshotReplaceOutcome,
  serializeCurrentQuizListToAiBuilderQuestions,
} from './aiBuilderQuestionSnapshot';
import {
  resolveAiGenerateDockErrorEvent,
  resolveAiGenerateDockRespond,
} from './aiGenerateDockOutcome';
import { buildDirectGenerateRequest } from './aiGenerateDockRequest';
import {
  type AiGenerateDockRegisteredSource,
  type AiGenerateDockRunInput,
  type AiGenerateDockRunResult,
  otherRejection,
  toDockSubmission,
  toRejectedResult,
  validateAiGenerateDockRunInput,
} from './aiGenerateDockRunInput';
import {
  type AppliedGenerationCounter,
  createAppliedGenerationCounter,
} from './aiGenerationCompletedTracking';
import { type ServerAiBuilderChatEventDto } from './serverAiBuilderChatDtos';
import { toQuestionSnapshotReplaceEvent } from './serverAiBuilderChatEvents';
import {
  isServerAiBuilderChatHttpError,
  type RegisterSource,
  type StreamTurn,
} from './serverAiBuilderChatTransport';
import {
  type AiBuilderBuilderContext,
  type AiBuilderChatQuestionSnapshotReplaceEvent,
} from './types';
import { ABORTED, untilAborted } from './untilAborted';

export type AppliedQuizListChange = Extract<
  QuestionSnapshotReplaceOutcome,
  { status: 'applied' }
>;

export type AiGenerateDockRunDependencies = {
  draftInstanceId: string;
  builderContext: AiBuilderBuilderContext;
  signal: AbortSignal;
  ensureSession: () => Promise<string | null>;
  getQuizList: () => BuildQuizListItem[];
  applyQuizList: (change: AppliedQuizListChange) => void;
  streamTurn: StreamTurn;
  registerSource: RegisterSource;
  createClientRequestId?: () => string;
  /** 이 실행이 쓸 세션을 확보했을 때 — 끝 계측이 실행 도중 확보한 세션 id 를 싣도록 알린다. */
  onSessionReady?: (sessionId: string) => void;
  /** 단계가 바뀔 때 — 화면이 「자료를 읽고 있어요」와 「문제를 만들고 있어요」를 가른다. */
  onPhase?: (phase: AiGenerateDockRunPhase) => void;
  /** 자료 등록을 마쳤을 때 — 호출부가 다시 시도 입력에 실어 재사용한다. */
  onSourceRegistered?: (registered: AiGenerateDockRegisteredSource) => void;
};

export const runAiGenerateDock = async (
  input: AiGenerateDockRunInput,
  dependencies: AiGenerateDockRunDependencies,
): Promise<AiGenerateDockRunResult> => {
  const { signal } = dependencies;

  const invalid = validateAiGenerateDockRunInput(input);
  if (invalid) return invalid;

  try {
    // 세션 생성이 늦어도 중단은 바로 끝낸다 — 생성 자체는 채팅과 함께 쓰는 공유 요청이라 끊지 않는다.
    const sessionId = await untilAborted(dependencies.ensureSession(), signal);
    if (sessionId === ABORTED || signal.aborted) return { kind: 'aborted' };
    if (!sessionId) return otherRejection('SESSION_UNAVAILABLE');
    dependencies.onSessionReady?.(sessionId);

    const sourceRef = await registerInputSource(input, sessionId, dependencies);
    if (signal.aborted) return { kind: 'aborted' };
    if (typeof sourceRef === 'object' && sourceRef !== null) return sourceRef;

    const request = buildDirectGenerateRequest(
      toDockSubmission(input, sourceRef ?? ''),
    );
    if (!request.ok) return otherRejection('DIRECT_GENERATE_INVALID');

    dependencies.onPhase?.('generating');

    const questions = serializeCurrentQuizListToAiBuilderQuestions(
      dependencies.getQuizList(),
    );
    const clientRequestId = (
      dependencies.createClientRequestId ?? createAiBuilderClientRequestId
    )();
    const events = await dependencies.streamTurn({
      sessionId,
      draftInstanceId: dependencies.draftInstanceId,
      clientRequestId,
      signal,
      sourceRefs: request.value.sourceRefs,
      directGenerate: request.value.directGenerate,
      builderContext: dependencies.builderContext,
      questions,
      questionListRevision: questionListRevisionOf(questions),
    });

    const generations = createAppliedGenerationCounter();
    try {
      return await consumeTurnEvents(events, dependencies, generations);
    } finally {
      // 채팅과 같은 「생성 완료」 — 문항이 들어온 generation 은 실행이 중단·오류로 끝나도 완료로 센다.
      generations.track({
        place: 'ai_generator_dock',
        promptType: sourceRef ? 'source' : 'query',
        sessionId,
        clientRequestId,
      });
    }
  } catch (error) {
    if (signal.aborted) return { kind: 'aborted' };
    return otherRejection(
      isServerAiBuilderChatHttpError(error) ? error.code ?? null : null,
    );
  }
};

/**
 * 이미 있는 등록을 쓸 수 있으면 그 source_ref(중단이면 결과), 없으면 null — 원본으로 등록할 차례다.
 * 다시 시도가 넘긴 등록은 바로 쓰고, 첨부할 때 시작한 사전 등록은 기다렸다 쓴다. 둘 다 같은 세션일
 * 때만 쓴다 — 세션이 바뀌었으면 그 ref 는 새 세션 registry 에 없다.
 */
const reuseRegisteredSource = async (
  { registeredSource, pendingRegistration }: AiGenerateDockRunInput,
  sessionId: string,
  dependencies: AiGenerateDockRunDependencies,
): Promise<string | AiGenerateDockRunResult | null> => {
  if (registeredSource?.sessionId === sessionId) {
    return registeredSource.sourceRef;
  }

  dependencies.onPhase?.('preparing_source');
  // 사전 등록은 실행이 가져간 뒤에도 끊지 않는다(다시 시도가 재사용한다) — 이 실행의 대기만 중단에 바로 끝낸다.
  const preregistered = pendingRegistration
    ? await untilAborted(
        pendingRegistration.catch(() => null),
        dependencies.signal,
      )
    : null;
  if (preregistered === ABORTED || dependencies.signal.aborted)
    return { kind: 'aborted' };
  if (preregistered?.sessionId !== sessionId) return null;

  // 다시 시도 입력에 실리도록 알린다 — 다음 실행은 기다리지 않고 바로 쓴다.
  dependencies.onSourceRegistered?.(preregistered);
  return preregistered.sourceRef;
};

/**
 * 자료 탭이면 등록한 source_ref, 주제 탭이면 null, 등록 실패면 결과.
 * 같은 세션에 앞서 등록한 자료는 그대로 쓰고, 첨부할 때 시작한 사전 등록은 기다렸다 쓴다.
 * 세션이 바뀌었거나 사전 등록이 실패했으면 원본으로 다시 등록한다.
 */
const registerInputSource = async (
  input: AiGenerateDockRunInput,
  sessionId: string,
  dependencies: AiGenerateDockRunDependencies,
): Promise<string | null | AiGenerateDockRunResult> => {
  const { source, sourceType } = input;
  if (sourceType === 'query' || source.kind === 'none') return null;

  const reused = await reuseRegisteredSource(input, sessionId, dependencies);
  if (reused !== null) return reused;

  const base = {
    sessionId,
    draftInstanceId: dependencies.draftInstanceId,
    purpose: 'generator' as const,
    signal: dependencies.signal,
  };

  try {
    const response =
      sourceType === 'file'
        ? source.kind === 'file'
          ? await dependencies.registerSource({
              ...base,
              sourceType,
              file: source.file,
              fileName: source.fileName,
            })
          : null
        : source.kind === 'link'
        ? await dependencies.registerSource({
            ...base,
            sourceType,
            url: source.url,
          })
        : null;

    if (!response) return otherRejection('DIRECT_GENERATE_INVALID');
    if (response.status !== 'ready') return { kind: 'not_generated' };

    dependencies.onSourceRegistered?.({
      sourceRef: response.source_ref,
      sessionId,
    });
    return response.source_ref;
  } catch (error) {
    if (dependencies.signal.aborted) return { kind: 'aborted' };
    if (!isServerAiBuilderChatHttpError(error)) return otherRejection(null);

    return toRejectedResult(
      resolveAiGenerateDockErrorEvent({
        type: 'error',
        code: error.code ?? '',
        reason: error.reason,
        usage: error.details.usage,
        retry_after_seconds: error.details.retryAfterSeconds,
        field: error.details.field,
      }),
    );
  }
};

const consumeTurnEvents = async (
  events: AsyncIterable<ServerAiBuilderChatEventDto>,
  dependencies: AiGenerateDockRunDependencies,
  generations: AppliedGenerationCounter,
): Promise<AiGenerateDockRunResult> => {
  let exceededQuizLimit = false;

  for await (const event of events) {
    if (event.type === 'questions_updated') {
      const snapshotEvent = toQuestionSnapshotReplaceEvent(event, 0);
      const change = resolveQuizListChange(
        snapshotEvent,
        dependencies.getQuizList(),
      );
      if (change.status === 'applied') {
        dependencies.applyQuizList(change);
        generations.add(
          snapshotEvent.generationId,
          snapshotEvent.changed_question_ids.length,
        );
      } else exceededQuizLimit = true;
      continue;
    }

    if (event.type === 'error') {
      return toRejectedResult(resolveAiGenerateDockErrorEvent(event));
    }

    if (event.type !== 'respond') continue;

    if (exceededQuizLimit) return otherRejection('QUESTION_LIMIT_EXCEEDED');
    const { outcome, usage } = resolveAiGenerateDockRespond(event);
    if (outcome.kind === 'success' || outcome.kind === 'partial') {
      return { ...outcome, usage };
    }
    if (outcome.kind === 'invalid_response') {
      return otherRejection('INVALID_RESPONSE');
    }
    return outcome;
  }

  if (dependencies.signal.aborted) return { kind: 'aborted' };
  // terminal 없이 끊긴 스트림
  return otherRejection(null);
};

/**
 * 전체 교체가 기본이다. 생성 중에 사용자가 목록을 고쳐 stale 이면, 새로 만든 문항만 뒤에
 * 붙인다 — 생성기 결과는 언제나 "기존 문제 뒤에 추가" 다.
 */
const resolveQuizListChange = (
  event: AiBuilderChatQuestionSnapshotReplaceEvent,
  currentQuizList: BuildQuizListItem[],
): Exclude<QuestionSnapshotReplaceOutcome, { status: 'stale' }> => {
  const replaced = applyQuestionSnapshotReplace(event, { currentQuizList });
  if (replaced.status !== 'stale') return replaced;
  return appendQuestionSnapshotChanges(event, { currentQuizList });
};
